import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import Icon from './Icon';
import Sheet from './Sheet';
import PhotoViewer from './PhotoViewer';
import { useApp } from '../lib/AppContext';
import { resizeImages } from '../lib/image';
import { LOCALE } from '../lib/format';
import { CATEGORIES, STATUS_LABELS, categoryLabel, feedbackContext } from '../lib/feedback';

// "Góp ý cho Macro Mate": the chat behind the floating button, one for the personal app and coach mode alike
// (Api::FeedbackThreadsController). Opening it goes straight into the latest thread still open, else a new
// one; "Góp ý mới" starts another and "Tất cả góp ý" lists them all, the old form's messages among them.
// FeedbackProvider keeps whether the button shows and the team's unread replies (its red dot), and opens the
// chat from anywhere: the button (with the screenshot it took), a meal's "Báo AI tính sai", a notification.

const FeedbackContext = createContext(null);
export const useFeedback = () => useContext(FeedbackContext);

const OWNER_BASE = '/me';
const MAX_PHOTOS = 3;
const GREEN = 'bg-[#17804f] text-white';

const time = (iso) => new Date(iso).toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' });
// "Hôm nay · 09:15", "Hôm qua · 12:41", else "24/10 · 12:41".
function dayLabel(iso) {
  const d = new Date(iso);
  const days = Math.round((new Date().setHours(0, 0, 0, 0) - new Date(iso).setHours(0, 0, 0, 0)) / 86400000);
  const day = days === 0 ? 'Hôm nay' : days === 1 ? 'Hôm qua' : d.toLocaleDateString(LOCALE, { day: 'numeric', month: 'numeric' });
  return `${day} · ${time(iso)}`;
}

function Attachments({ items, onView }) {
  if (!items?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((a, i) => (
        <button key={a.url} type="button" onClick={() => onView(items, i)} aria-label={a.kind === 'screenshot' ? 'Xem ảnh màn hình' : `Xem ảnh ${i + 1}`}>
          <img src={a.url} alt="" className={`rounded-[10px] border border-line object-cover ${a.kind === 'screenshot' ? 'h-24 w-14' : 'size-20'}`} />
        </button>
      ))}
    </div>
  );
}

function Bubble({ message, onView }) {
  if (message.sender === 'user') {
    return (
      <div className="flex flex-col items-end gap-1">
        <Attachments items={message.attachments} onView={onView} />
        {message.body && <p className={`max-w-[85%] whitespace-pre-line rounded-[16px] rounded-br-[4px] px-3 py-2 text-[15px] ${GREEN}`}>{message.body}</p>}
      </div>
    );
  }
  const staff = message.sender === 'staff';
  return (
    <div className="flex items-end gap-2">
      {staff && (
        <span className="grid size-7 shrink-0 place-items-center rounded-full bg-[#15261f] text-xs font-bold text-[#6fd3a0]">
          {(message.staff_name || 'M').trim()[0].toUpperCase()}
        </span>
      )}
      <div className="flex max-w-[85%] flex-col gap-1">
        {staff && <span className="text-[11px] font-semibold text-muted">{message.staff_name} · Macro Mate</span>}
        <Attachments items={message.attachments} onView={onView} />
        {message.body && <p className="whitespace-pre-line rounded-[16px] rounded-bl-[4px] bg-base-200 px-3 py-2 text-[15px]">{message.body}</p>}
      </div>
    </div>
  );
}

function Messages({ thread, onConfirm, busy, onView }) {
  const firstUser = thread.messages.findIndex((m) => m.sender === 'user');
  let lastDay = null;
  return (
    <div className="flex flex-col gap-2.5">
      {thread.messages.map((m, i) => {
        // A day and time over each group sent more than an hour after the one before.
        const gap = !lastDay || new Date(m.created_at) - new Date(lastDay) > 3600000;
        lastDay = m.created_at;
        return (
          <div key={m.id} className="flex flex-col gap-1">
            {gap && <p className="py-1 text-center text-[11px] text-muted">{dayLabel(m.created_at)}</p>}
            <Bubble message={m} onView={onView} />
            {i === firstUser && (
              <p className="flex items-center justify-end gap-1 text-[11px] text-muted">
                <span className="size-1.5 rounded-full bg-error" /> {categoryLabel(thread.category)} · Mã #{thread.code}
              </p>
            )}
          </div>
        );
      })}
      {thread.status === 'resolved' && (
        <div className="flex flex-col gap-2 pl-9">
          <span className="w-fit rounded-full bg-[#e3f1e9] px-2.5 py-0.5 text-xs font-semibold text-[#17804f]">
            <Icon name="check" className="inline size-3" /> Đã xử lý
          </span>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="h-10 rounded-full border border-line px-4 text-sm font-semibold" disabled={busy} onClick={() => onConfirm(true)}>Đã ổn rồi 👍</button>
            <button type="button" className="h-10 rounded-full border border-line px-4 text-sm font-semibold" disabled={busy} onClick={() => onConfirm(false)}>Vẫn còn lỗi</button>
          </div>
        </div>
      )}
      {thread.status === 'closed' && <p className="py-1 text-center text-xs text-muted">Góp ý đã đóng · nhắn tiếp để mở lại</p>}
    </div>
  );
}

function ThreadList({ threads, onOpen }) {
  return (
    <ul className="divide-y divide-line rounded-[20px] border border-line px-4">
      {threads.map((t) => (
        <li key={t.id}>
          <button type="button" className="flex min-h-16 w-full items-center gap-3 py-2 text-left" onClick={() => onOpen(t.id)}>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2 text-xs text-muted">
                <span className="font-semibold">#{t.code}</span> · {categoryLabel(t.category)} · {STATUS_LABELS[t.status]}
              </span>
              <span className="block truncate text-[15px]">{t.preview || 'Ảnh đính kèm'}</span>
            </span>
            {t.unread > 0 && <span className="size-2.5 shrink-0 rounded-full bg-error" aria-label="Có trả lời mới" />}
            <Icon name="chevronRight" className="size-4 shrink-0 text-muted" />
          </button>
        </li>
      ))}
    </ul>
  );
}

// The bottom of the chat: the screenshot (with its switch), added photos, the message and send.
function Composer({ shot, shotUrl, sendShot, setSendShot, screen, photos, setPhotos, body, setBody, busy, onSend, placeholder }) {
  const addPhotos = async (event) => {
    const files = Array.from(event.target.files);
    event.target.value = '';
    const room = MAX_PHOTOS - photos.length;
    if (room > 0) setPhotos([...photos, ...(await resizeImages(files.slice(0, room)))]);
  };
  const canSend = !busy && (body.trim() || photos.length || (shot && sendShot));
  // The box grows with the message (up to max-h-32), so a message written for them shows whole.
  const field = useRef(null);
  useLayoutEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [body]);
  return (
    <div className="sticky bottom-0 -mx-5 flex flex-col gap-2 border-t border-line bg-base-100 px-5 pt-3">
      {shot && (
        <label className="flex items-center gap-3 rounded-[14px] bg-base-200 p-2">
          <img src={shotUrl} alt="Ảnh màn hình đang xem" className="h-12 w-8 rounded-[6px] border border-line object-cover object-top" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">Kèm ảnh màn hình đang xem</span>
            <span className="block truncate text-xs text-muted">{screen}</span>
          </span>
          <input type="checkbox" className="toggle toggle-success" checked={sendShot} onChange={(e) => setSendShot(e.target.checked)} />
        </label>
      )}
      {photos.length > 0 && (
        <div className="flex gap-2">
          {photos.map((p, i) => (
            <div key={`${p.name}-${i}`} className="relative">
              <img src={URL.createObjectURL(p)} alt={`Ảnh ${i + 1}`} className="size-14 rounded-[10px] object-cover" />
              <button type="button" aria-label={`Bỏ ảnh ${i + 1}`} onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                className="absolute -right-1.5 -top-1.5 grid size-6 place-items-center rounded-full bg-neutral text-neutral-content">
                <Icon name="x" className="size-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); if (canSend) onSend(); }}>
        <label className={`grid size-11 shrink-0 place-items-center rounded-full bg-base-200 ${photos.length >= MAX_PHOTOS ? 'opacity-40' : ''}`}
          aria-label={`Thêm ảnh (tối đa ${MAX_PHOTOS})`}>
          <Icon name="photo" className="size-5" />
          <input type="file" accept="image/*" multiple className="sr-only" disabled={photos.length >= MAX_PHOTOS} onChange={addPhotos} />
        </label>
        <textarea ref={field} rows={1} maxLength={4000} value={body} onChange={(e) => setBody(e.target.value)} placeholder={placeholder}
          className="textarea max-h-32 min-h-11 flex-1 resize-none rounded-[22px] border-line py-2.5 text-base leading-snug" />
        <button type="submit" aria-label="Gửi" disabled={!canSend} className={`grid size-11 shrink-0 place-items-center rounded-full ${GREEN} disabled:opacity-40`}>
          {busy ? <span className="loading loading-spinner loading-sm" /> : <Icon name="send" className="size-5" />}
        </button>
      </form>
    </div>
  );
}

function FeedbackSheet({ request, onClose }) {
  const { api, profile, native } = useApp();
  // "Mua Pro" is for the website only: the iOS/Android app may only sell Pro through In-App Purchase.
  const categories = native ? CATEGORIES.filter((c) => c.id !== 'buy_pro') : CATEGORIES;
  const { setFeedback } = useFeedback();
  const [view, setView] = useState('loading'); // loading | new | thread | list
  const [threads, setThreads] = useState([]);
  const [thread, setThread] = useState(null);
  const [category, setCategory] = useState(null);
  const [body, setBody] = useState('');
  const [photos, setPhotos] = useState([]);
  const [sendShot, setSendShot] = useState(true);
  const [shot, setShot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(null);
  const endRef = useRef(null);
  const shotUrl = useMemo(() => (shot ? URL.createObjectURL(shot) : null), [shot]);
  useEffect(() => () => shotUrl && URL.revokeObjectURL(shotUrl), [shotUrl]);

  const setUnread = useCallback((unread) => setFeedback((f) => ({ ...f, unread })), [setFeedback]);
  const showThread = useCallback(async (id) => {
    setError(null);
    const data = await api.get(`/feedback_threads/${id}`);
    setThread(data);
    setUnread(data.unread_total);
    setThreads((list) => list.map((t) => (t.id === data.id ? { ...t, unread: 0, status: data.status } : t)));
    setView('thread');
  }, [api, setUnread]);
  const startNew = (preset = null) => { setCategory(preset); setThread(null); setView('new'); };

  useEffect(() => {
    if (!request) return;
    setShot(request.screenshot || null);
    setSendShot(true);
    // A message written for them (e.g. "Mua Pro"), to edit before sending.
    setBody(request.body || '');
    setPhotos([]);
    setError(null);
    setView('loading');
    api.get('/feedback_threads').then((data) => {
      setThreads(data.threads);
      setUnread(data.unread);
      const live = data.threads.find((t) => t.status !== 'closed');
      if (request.threadId) return showThread(request.threadId);
      if (request.category || !live) return startNew(request.category || null);
      return showThread(live.id);
    }).catch((e) => { setError(e.message); setView('new'); });
  }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [thread, view]);

  const form = () => {
    const data = new FormData();
    data.append('body', body.trim());
    if (shot && sendShot) data.append('screenshot', shot);
    photos.forEach((p) => data.append('photos[]', p));
    return data;
  };
  const sent = (data) => {
    setThread(data);
    setUnread(data.unread_total);
    setBody('');
    setPhotos([]);
    setShot(null); // the screenshot goes once
    setView('thread');
  };
  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const send = () => run(async () => {
    if (view === 'thread') {
      sent(await api.post(`/feedback_threads/${thread.id}/message`, form()));
      return;
    }
    const data = form();
    data.append('category', category || 'other');
    data.append('mode', request.mode);
    Object.entries(request.context || {}).forEach(([k, v]) => v && data.append(`context[${k}]`, v));
    if (category === 'ai_wrong' && request.meal) data.append('meal_id', request.meal.id);
    const created = await api.post('/feedback_threads', data);
    setThreads((list) => [created, ...list]);
    sent(created);
  });
  const confirm = (fixed) => run(async () => sent(await api.post(`/feedback_threads/${thread.id}/confirm`, { fixed })));
  const viewPhotos = (items, index) => setViewing({ photos: items.map((a, i) => ({ id: i, url: a.url })), index });

  const firstName = (profile?.name || '').trim().split(/\s+/).pop();
  const picked = CATEGORIES.find((c) => c.id === category);
  const navLink = 'flex min-h-10 items-center gap-1 text-sm font-semibold text-brand-text';

  return (
    <Sheet open={!!request} onClose={onClose} title="Góp ý cho Macro Mate"
      badge={<span className="text-xs text-muted">Đội ngũ thường trả lời trong 1 ngày</span>}>
      <div data-feedback-ui className="flex min-h-[50dvh] flex-col gap-3">
        {view !== 'loading' && (
          <div className="flex items-center justify-between gap-2">
            {view !== 'list' && threads.length > 0
              ? <button type="button" className={navLink} onClick={() => setView('list')}><Icon name="chevronLeft" className="size-4" /> Tất cả góp ý</button>
              : <span />}
            {view !== 'new' && <button type="button" className={navLink} onClick={() => startNew()}><Icon name="plus" className="size-4" /> Góp ý mới</button>}
          </div>
        )}

        {view === 'loading' && <div className="skeleton h-40 rounded-[20px]" />}
        {view === 'list' && <ThreadList threads={threads} onOpen={(id) => run(() => showThread(id))} />}
        {view === 'thread' && thread && <Messages thread={thread} onConfirm={confirm} busy={busy} onView={viewPhotos} />}
        {view === 'new' && (
          <div className="flex flex-col gap-2.5">
            <p className="max-w-[85%] rounded-[16px] rounded-bl-[4px] bg-base-200 px-3 py-2 text-[15px]">
              Chào {firstName || 'bạn'} 👋 Bạn muốn góp ý hay báo lỗi gì? Chọn nhanh một mục hoặc nhắn thẳng bên dưới.
            </p>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Loại góp ý">
              {categories.map((c) => (
                <button key={c.id} type="button" role="radio" aria-checked={category === c.id} onClick={() => setCategory(category === c.id ? null : c.id)}
                  className={`h-9 rounded-full border px-3 text-sm font-semibold ${category === c.id ? 'border-[#17804f] bg-[#e3f1e9] text-[#17804f]' : 'border-line'}`}>
                  {c.label}
                </button>
              ))}
            </div>
            {picked && <p className="max-w-[85%] rounded-[16px] rounded-bl-[4px] bg-base-200 px-3 py-2 text-[15px]">{picked.hint}</p>}
            {category === 'ai_wrong' && request?.meal && (
              <div className="flex items-center gap-3 rounded-[14px] border border-line p-2">
                {request.meal.photos?.[0] && <img src={request.meal.photos[0].thumb_url} alt="" className="size-12 rounded-[10px] object-cover" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">{request.meal.title || request.meal.description || 'Bữa ăn'}</span>
                  <span className="block text-xs text-muted">Gửi kèm ảnh món và kết quả AI</span>
                </span>
              </div>
            )}
          </div>
        )}
        <div ref={endRef} />
        {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
        <div className="flex-1" />
        {view !== 'list' && view !== 'loading' && (
          <Composer shot={shot} shotUrl={shotUrl} sendShot={sendShot} setSendShot={setSendShot} screen={request?.context?.screen}
            photos={photos} setPhotos={setPhotos} body={body} setBody={setBody} busy={busy} onSend={send}
            placeholder={view === 'thread' ? 'Nhắn tiếp…' : 'Nhập góp ý của bạn…'} />
        )}
      </div>
      <PhotoViewer photos={viewing?.photos || []} index={viewing ? viewing.index : null}
        onChange={(i) => setViewing((v) => ({ ...v, index: i }))} onClose={() => setViewing(null)} />
    </Sheet>
  );
}

// Above the routes, inside the router: one chat for the whole app.
export function FeedbackProvider({ initial, children }) {
  const { native } = useApp();
  const { pathname, search } = useLocation();
  const [feedback, setFeedback] = useState(initial || { show_button: false, unread: 0 });
  const [request, setRequest] = useState(null);
  const where = useRef({ pathname, search });
  where.current = { pathname, search };

  // { mode: personal | coach, screenshot, category, meal, threadId, body (a message to start from) }
  const openFeedback = useCallback(async (options = {}) => {
    const { pathname: p, search: s } = where.current;
    const pathAfterBase = p.startsWith(OWNER_BASE) ? p.slice(OWNER_BASE.length) || '/' : p;
    const context = await feedbackContext({ native, pathAfterBase, search: s });
    setRequest({ mode: 'personal', ...options, context });
  }, [native]);
  const value = useMemo(() => ({ feedback, setFeedback, openFeedback }), [feedback, openFeedback]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <FeedbackSheet request={request} onClose={() => setRequest(null)} />
    </FeedbackContext.Provider>
  );
}
